import * as Styled from "./PlaylistDetail.styled";
import { useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { playlistsService } from "../../../../services";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { createTheme, ThemeProvider } from "@mui/material";
import DefaultThumbnailImage from "../../../../assets/images/default_thumbnail.jpeg";
import { ReactComponent as TemplateIcon } from "../../../../assets/images/icons/template-icon.svg";
import { ReactComponent as ReorderIcon } from "../../../../assets/images/icons/reorder-icon.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/images/icons/info-icon .svg";
import AdPreviewImage from "../../../../assets/images/layout/AdPreviewImage_01.png";
import { Image } from "react-bootstrap";
import { Reorder } from "./reorder";
import { IPlaylistMediaItem, IPlaylistRequestDto } from "../../../../libs";
import { SelectTemplate } from "./select-template";
import SearchIcon from "../../../../assets/images/icons/search.svg";
import { toast } from "react-toastify";
import { ChevronLeft, XLg } from "react-bootstrap-icons";
import { Loader } from "../../../ui";
import dayjs from "dayjs";

export const PlaylistDetail: React.FC = () => {
    const navigate = useNavigate();
    const { playlistId } = useParams<{ playlistId: string }>();
    const [playlist, setPlaylist] = useState<any>(null);
    const [data, setData] = useState<IPlaylistMediaItem[]>([]);
    const [originalData, setoriginalData] = useState<IPlaylistMediaItem[]>([]);
    const [showRecorder, setShowRecorder] = useState(false)
    const [showTemplate, setShowTemplate] = useState(false)
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    const fetchPlaylist = useCallback(async () => {
        if (playlistId) {
            setLoading(true);
            try {
                const playlistResponse = await playlistsService.getPlaylistById(playlistId);

                if (playlistResponse) {
                    const formattedMediaItems = (playlistResponse.mediaItems || []).map((item: any, index) => ({
                        ...item,
                        publishedDateFormatted: dayjs(item.publishedDate).format("MMM D, YYYY"),
                        numb: index,
                        producers: item.producers || "Unknown",
                    }));

                    setPlaylist(playlistResponse);
                    setData(formattedMediaItems);
                    setoriginalData(formattedMediaItems);
                }
            } catch (error) {
                toast.error("Error fetching playlist.");
            } finally {
                setLoading(false);
            }
        }
    }, [playlistId]);

    const filteredData = React.useMemo(() => {
        return data.filter(item =>
            item.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, data]);

    useEffect(() => {
        fetchPlaylist();
    }, [playlistId]);

    const columns = React.useMemo<MRT_ColumnDef<IPlaylistMediaItem>[]>(() => [
        {
            header: "#",
            headerAlign: 'center',

            Cell: ({ row }) => {
                return (
                    <Styled.PlaylistNumberHolderWrapper>
                        <Styled.PlaylistNumberHolder>
                            {row.index + 1}
                        </Styled.PlaylistNumberHolder>
                    </Styled.PlaylistNumberHolderWrapper>
                );
            },
            enableSorting: false,
            size: 40,
        },
        {
            header: "Thumbnail",
            accessorKey: "thumbnail",
            size: 87,
            Cell: ({ row }) => (
                <Styled.ThumbnailImage
                    src={row.original.thumbnail || DefaultThumbnailImage}
                    alt="Thumbnail"
                />
            ),
        },
        {
            header: "Title",
            accessorKey: "title",
            size: 200,
            Cell: ({ row }) => <span style={{ fontSize: "14px", fontWeight: 700 }}>{row.original.title}</span>,
        },
        {
            header: "Created Date",
            accessorKey: "publishedDateFormatted",
            size: 120,
            Cell: ({ row }) => (
                <span style={{ fontSize: "14px", fontWeight: 400, color: "#9F9F9F99" }}>
                    {row.original.publishedDateFormatted}
                </span>
            ),
        },
    ], []);


    const table = useMaterialReactTable({
        autoResetPageIndex: false,
        columns,
        data: filteredData,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableFilters: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enablePagination: true,
        manualPagination: false,
        paginationDisplayMode: 'pages',
        renderTopToolbar: '',
        initialState: {
            showColumnFilters: false,
            showGlobalFilter: false,
            showToolbarDropZone: false,
            density: 'comfortable',
            pagination: {
                pageIndex: 0,
                pageSize: 10,
            },
        },

        muiPaginationProps: {
            showRowsPerPage: true,
            showFirstButton: true,
            showLastButton: true,
        },
        muiTableHeadRowProps: {
            sx: {
                borderBottom: '2px solid #1d1f24',
            },
        },

        positionPagination: "bottom",
        muiTableBodyCellProps: {
            sx: {
                borderBottom: 'none !important',
            },
        },
        mrtTheme: {
            baseBackgroundColor: "#0F1013",
            draggingBorderColor: "#b6f84d",
        },
    });


    const theme = createTheme({
        palette: {
            mode: "dark",
        },
    });

    function hasOrderChanged(arr1, arr2) {
        if (arr1.length !== arr2.length) return true;

        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i].id !== arr2[i].id) {
                return true;
            }
        }
        return false;
    }

    const updatePlaylist = useCallback(async () => {
        if (hasOrderChanged(data, originalData)) {
            if (playlist?.playlistId) {
                const updatedPlaylist: IPlaylistRequestDto = {
                    title: playlist.title,
                    description: playlist.description,
                    thumbnails: playlist.thumbnails,
                    audience: playlist.audience,
                    visibility: playlist.visibility,
                    playliststatus: playlist.playliststatus,
                    steps: playlist.steps,
                    category: "",
                    mediaItems: data.map((item) => item.mediaId),
                    templateId: playlist?.templateId
                };
                try {
                    setLoading(true);
                    const response = await playlistsService.updatePlaylist(updatedPlaylist, playlist.playlistId);
                    await fetchPlaylist();
                    toast.success(response?.message || "Playlist updated successfully!");
                } catch (error: any) {
                    const errorMessage = error?.response?.data?.error?.message || error?.message || "Failed to update playlist.";
                    toast.error(errorMessage);
                } finally {
                    setLoading(false);
                }
            }
        }

    }, [data, originalData, playlist, fetchPlaylist]);

    const reset = () => {
        setData(originalData
        )
    }
    return (
        loading ? (
            <Loader />
        ) : (
            <>
                <Reorder show={showRecorder} selectedVideos={playlist} mediaItems={data} setmediaItems={setData} onHide={() => {
                    setShowRecorder(false)
                }} />
                <SelectTemplate show={showTemplate} onHide={() => setShowTemplate(false)} originalData={originalData} data={data} setData={setData} setLoading={setLoading} fetchPlaylist={fetchPlaylist} playlist={playlist} />
                <Styled.PageLayout>
                    <Styled.LeftSection>
                        <ThemeProvider theme={theme}>
                            <Styled.StyledContainer>
                                <Styled.Header>
                                    <Styled.HeaderLeftSection>
                                        <Styled.TitleRow>
                                            <Styled.PlaylistTitle title={playlist?.title}>
                                                {playlist?.title}
                                            </Styled.PlaylistTitle>
                                        </Styled.TitleRow>
                                    </Styled.HeaderLeftSection>
                                    <Styled.HeaderRightSection>
                                        <Styled.CustomButton variant="outline-secondary" onClick={() => setShowTemplate(true)}>
                                            <Styled.IconWrapper>
                                                <TemplateIcon />
                                            </Styled.IconWrapper>
                                            <Styled.Label>Template</Styled.Label>
                                        </Styled.CustomButton>
                                        <Styled.CustomButton variant="outline-secondary" onClick={() => setShowRecorder(true)}>
                                            <Styled.IconWrapper>
                                                <ReorderIcon />
                                            </Styled.IconWrapper>
                                            <Styled.Label>Reorder</Styled.Label>
                                        </Styled.CustomButton>
                                        <Styled.CustomButton variant="outline-secondary" onClick={() => { reset(); navigate(-1); }}>
                                            <Styled.IconWrapper>
                                                <XLg />
                                            </Styled.IconWrapper>
                                            <Styled.Label>Cancel</Styled.Label>
                                        </Styled.CustomButton>
                                        <Styled.SaveButton variant="primary" onClick={() => { updatePlaylist() }}>Save</Styled.SaveButton>
                                    </Styled.HeaderRightSection>
                                </Styled.Header>

                                <Styled.Description>
                                    <Styled.DescriptionLeftSection>
                                        <Styled.InfoIconStyled>
                                            <InfoIcon />
                                        </Styled.InfoIconStyled>

                                        There are some videos that are repeated in other content folders.
                                    </Styled.DescriptionLeftSection>
                                    <Styled.DescriptionRightSection>
                                        <Styled.DashboardSearchWrapper>
                                            <Styled.SearchBarWrapper>
                                                <Styled.SearchIcon src={SearchIcon} alt="Search" />
                                                <Styled.SearchInput
                                                    name="search"
                                                    type="text"
                                                    placeholder="Search by title"
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                            </Styled.SearchBarWrapper>
                                        </Styled.DashboardSearchWrapper>
                                    </Styled.DescriptionRightSection>
                                </Styled.Description>

                                <Styled.TableWrapper>
                                    {filteredData.length === 0 ? (
                                        <Styled.NoResultsText>No results found.</Styled.NoResultsText>
                                    ) : (
                                        <MaterialReactTable table={table} />

                                    )}

                                </Styled.TableWrapper>
                            </Styled.StyledContainer>
                        </ThemeProvider>
                    </Styled.LeftSection>

                    <Styled.RightSection>
                        <Styled.PreviewWrapper>
                            <Styled.PreviewHeader>Preview</Styled.PreviewHeader>
                            <Image src={AdPreviewImage} />
                        </Styled.PreviewWrapper>
                    </Styled.RightSection>
                </Styled.PageLayout>

            </>
        )
    )
};
